/**
 * Form Data Utilities for FormCrafter
 * Functions for parsing and manipulating form data
 */

import { parseFilledFormValue } from '@iqvia-technology-solutions/formcrafter';

/**
 * Parses filled form value to extract data by subform ID
 * Handles both new nested structure and legacy format
 *
 * New structure: { formId: { subformId: { fieldId: value } } }
 * Legacy structure: [{ subformId: { fieldId: value } }] or { subformId: { fieldId: value } }
 *
 * @param {Object} filledForm - The filled form object with value property
 * @param {number|string} formId - Optional form ID for nested structure extraction
 * @returns {Object} - Flattened data by subformId: { [subformId]: { fieldId: value } }
 */
export const parseFormData = (filledForm, formId = null) => {
  if (!filledForm?.value) return {};

  try {
    const parsed =
      typeof filledForm.value === 'string'
        ? JSON.parse(filledForm.value)
        : filledForm.value;

    // Check if it's the new nested structure
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const firstKey = Object.keys(parsed)[0];
      const firstValue = firstKey ? parsed[firstKey] : null;

      // Check if it's nested: { formId: { subformId: { fieldId: value } } }
      if (firstValue && typeof firstValue === 'object') {
        const firstSubKey = Object.keys(firstValue)[0];
        const firstSubValue = firstSubKey ? firstValue[firstSubKey] : null;

        // Detect field data vs subform maps.  Field data contains at least one
        // key starting with 'field_', a primitive value, or an array (repeater).
        // Subform maps have only plain-object values keyed by subform ID.
        const isFieldData = (obj) => {
          if (!obj || typeof obj !== 'object') return false;
          const keys = Object.keys(obj);
          return (
            keys.length > 0 &&
            keys.some(
              (k) =>
                k.startsWith('field_') ||
                typeof obj[k] !== 'object' ||
                Array.isArray(obj[k])
            )
          );
        };

        // If formId provided, check if nested data exists for it
        const potentialNestedData = formId ? parsed[formId] : null;
        const isNestedFormat =
          potentialNestedData &&
          typeof potentialNestedData === 'object' &&
          !isFieldData(potentialNestedData);

        if (isNestedFormat && Object.keys(potentialNestedData).length > 0) {
          return potentialNestedData;
        }

        // Only flatten when the data is genuinely nested (formId → subformId → fields).
        // Check ALL values, not just the first — an empty first subform ({}) does not
        // mean the structure is nested when other values contain field data.
        if (!Object.values(parsed).some((val) => isFieldData(val))) {
          const flatData = {};
          for (const fId of Object.keys(parsed)) {
            const formSubforms = parsed[fId];
            if (formSubforms && typeof formSubforms === 'object') {
              for (const subformId of Object.keys(formSubforms)) {
                flatData[subformId] = {
                  ...(flatData[subformId] || {}),
                  ...formSubforms[subformId],
                };
              }
            }
          }
          return flatData;
        }
      }
      // Flat object structure { subformId: fieldData } - return as-is
      return parsed;
    }

    // Legacy array format - use library parser
    const legacyResult = parseFilledFormValue(filledForm.value);
    return legacyResult;
  } catch (error) {
    console.error('Error parsing filled form value:', error);
    return {};
  }
};

/**
 * Checks if a value is empty (for validation)
 * Handles undefined, null, empty strings, whitespace-only strings, and empty arrays
 * @param {any} value - Value to check
 * @returns {boolean}
 */
export const isValueEmpty = (value) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0);

/**
 * Normalizes a checkbox value to array format
 * @param {any} value - Value to normalize
 * @returns {Array}
 */
export const normalizeCheckboxValue = (value) => {
  if (Array.isArray(value)) return value;
  if (value) return [value];
  return [];
};

/**
 * Normalizes a boolean value from various formats
 * @param {any} value - Value to normalize
 * @returns {boolean}
 */
export const normalizeBooleanValue = (value) => {
  return value === true || value === 'true' || value === 1 || value === '1';
};
